import { PMTiles } from 'pmtiles'
import type { StyleSpecification, LayerSpecification } from 'maplibre-gl'

export type MapTheme = 'dark' | 'light'

// Banshee dark theme colors
const darkColors = {
  bg: '#0d1b2a',
  land: '#0b1626',
  landuse: '#102c2a',
  water: '#0b2c45',
  roadCasing: '#08101e',
  road: '#94a3b8',
  roadMajor: '#e5e7eb',
  building: '#1b263b',
  boundary: '#27486a',
  text: '#e5e7eb',
  muted: '#94a3b8',
  halo: '#0d1b2a',
  ghost: '#7fffe4',
  ghostDark: '#28d8a1',
  clusterText: '#0d1b2a',
  popupBg: '#1b263b',
  popupText: '#e5e7eb',
  popupMuted: '#94a3b8',
}

// Banshee light theme colors
const lightColors = {
  bg: '#f8fafc',
  land: '#f1f5f9',
  landuse: '#dcfce7',
  water: '#bae6fd',
  roadCasing: '#e2e8f0',
  road: '#64748b',
  roadMajor: '#334155',
  building: '#e2e8f0',
  boundary: '#94a3b8',
  text: '#1e293b',
  muted: '#64748b',
  halo: '#ffffff',
  ghost: '#10b981',
  ghostDark: '#059669',
  clusterText: '#ffffff',
  popupBg: '#ffffff',
  popupText: '#1e293b',
  popupMuted: '#64748b',
}

export type MapColors = typeof darkColors

export function getMapColors(theme: MapTheme = 'dark'): MapColors {
  return theme === 'light' ? lightColors : darkColors
}

// Layer name aliases for different PMTiles schemas
const layerAliases: Record<string, string[]> = {
  land: ['earth', 'land', 'landcover'],
  landuse: ['landuse', 'land_use', 'parks'],
  water: ['water', 'waterway'],
  boundaries: ['boundary', 'boundaries', 'administrative'],
  buildings: ['building', 'buildings'],
  roads: ['transportation', 'roads'],
  roadNames: ['transportation_name', 'road_name', 'roads_name'],
  places: ['places', 'place', 'poi'],
}

// Name field coalesce for labels
const coalesceName = [
  'coalesce',
  ['get', 'name:en'],
  ['get', 'name'],
  ['get', 'name_en'],
  ['get', 'name_int'],
]

/**
 * Detect available vector layers from PMTiles metadata
 */
export async function getVectorLayers(pmtilesUrl: string): Promise<Set<string>> {
  try {
    const p = new PMTiles(pmtilesUrl)
    const metadata = await p.getMetadata()
    const layers = (metadata as { vector_layers?: { id: string }[] })?.vector_layers?.map(l => l.id) ?? []
    return new Set(layers)
  } catch {
    return new Set()
  }
}

/**
 * Pick the first available layer from aliases
 */
function pickLayer(available: Set<string>, key: string): string | null {
  const aliases = layerAliases[key] ?? [key]
  return aliases.find(alias => available.has(alias)) ?? null
}

/**
 * Create map style for PMTiles basemap
 */
export async function createMapStyle(pmtilesUrl: string, theme: MapTheme = 'dark'): Promise<StyleSpecification> {
  const available = await getVectorLayers(pmtilesUrl)
  const colors = getMapColors(theme)

  const layers: LayerSpecification[] = [
    { id: 'background', type: 'background', paint: { 'background-color': colors.bg } },
  ]

  const land = pickLayer(available, 'land')
  if (land) {
    layers.push({
      id: 'land',
      type: 'fill',
      source: 'basemap',
      'source-layer': land,
      paint: { 'fill-color': colors.land },
    })
  }

  const landuse = pickLayer(available, 'landuse')
  if (landuse) {
    layers.push({
      id: 'landuse',
      type: 'fill',
      source: 'basemap',
      'source-layer': landuse,
      paint: { 'fill-color': colors.landuse, 'fill-opacity': 0.65 },
    })
  }

  const water = pickLayer(available, 'water')
  if (water) {
    layers.push({
      id: 'water',
      type: 'fill',
      source: 'basemap',
      'source-layer': water,
      paint: { 'fill-color': colors.water },
    })
  }

  const roads = pickLayer(available, 'roads')
  if (roads) {
    layers.push({
      id: 'roads-casing',
      type: 'line',
      source: 'basemap',
      'source-layer': roads,
      paint: {
        'line-color': colors.roadCasing,
        'line-opacity': 0.75,
        'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0, 10, 1.6, 14, 5.2, 16, 10],
      },
    })
    layers.push({
      id: 'roads',
      type: 'line',
      source: 'basemap',
      'source-layer': roads,
      paint: {
        'line-color': [
          'case',
          ['in', ['get', 'class'], ['literal', ['motorway', 'trunk', 'primary']]],
          colors.roadMajor,
          colors.road,
        ],
        'line-opacity': 0.75,
        'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0, 10, 1.1, 14, 3.6, 16, 7],
      },
    })
  }

  const buildings = pickLayer(available, 'buildings')
  if (buildings) {
    layers.push({
      id: 'buildings',
      type: 'fill',
      source: 'basemap',
      'source-layer': buildings,
      minzoom: 13,
      paint: { 'fill-color': colors.building, 'fill-opacity': 0.65 },
    })
  }

  const boundaries = pickLayer(available, 'boundaries')
  if (boundaries) {
    layers.push({
      id: 'boundaries',
      type: 'line',
      source: 'basemap',
      'source-layer': boundaries,
      paint: { 'line-color': colors.boundary, 'line-opacity': 0.55, 'line-width': 1 },
    })
  }

  const places = pickLayer(available, 'places')
  if (places) {
    layers.push({
      id: 'place-labels',
      type: 'symbol',
      source: 'basemap',
      'source-layer': places,
      layout: {
        'text-field': coalesceName as unknown as string,
        'text-font': ['Noto Sans Regular'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 2, 10, 6, 12, 10, 16, 14, 22],
        'text-max-width': 8,
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': ['interpolate', ['linear'], ['zoom'], 2, colors.muted, 6, colors.text],
        'text-halo-color': colors.halo,
        'text-halo-width': 1.25,
        'text-opacity': 0.95,
      },
    })
  }

  const roadNames = pickLayer(available, 'roadNames')
  if (roadNames) {
    layers.push({
      id: 'road-labels',
      type: 'symbol',
      source: 'basemap',
      'source-layer': roadNames,
      minzoom: 12,
      layout: {
        'symbol-placement': 'line',
        'text-field': coalesceName as unknown as string,
        'text-font': ['Noto Sans Regular'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 12, 10, 16, 12, 18, 14],
      },
      paint: {
        'text-color': colors.text,
        'text-halo-color': colors.halo,
        'text-halo-width': 1,
        'text-opacity': 0.8,
      },
    })
  }

  return {
    version: 8,
    glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
    sources: {
      basemap: {
        type: 'vector',
        url: `pmtiles://${pmtilesUrl}`,
      },
    },
    layers,
  }
}

// Legacy export for backward compatibility
export const mapColors = darkColors
